import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Heart, Globe, ShieldCheck, Sparkles, Users, Rocket, ArrowUpRight,
  Lightbulb, HandshakeIcon, TrendingUp, Target, Award,
} from "lucide-react";
import PublicNav from "../components/PublicNav";
import PublicFooter from "../components/PublicFooter";
import SeoHead from "../components/SeoHead";

const VALUE_KEYS = [
  { key: "excellence",     icon: Sparkles,     color: "var(--mod-talent)" },
  { key: "innovation",     icon: Lightbulb,    color: "var(--mod-coach-ai)" },
  { key: "inclusion",      icon: Users,        color: "var(--mod-emploi)" },
  { key: "transparence",   icon: ShieldCheck,  color: "var(--mod-admin)" },
  { key: "collaboration",  icon: HandshakeIcon,color: "var(--afr-primary)" },
  { key: "responsabilite", icon: Target,       color: "var(--mod-freelance)" },
  { key: "confiance",      icon: Heart,        color: "var(--mod-entrepreneur)" },
  { key: "impact",         icon: TrendingUp,   color: "var(--mod-investissement)" },
];

const TIMELINE_YEARS = ["2026", "2028", "2032", "2040", "2045"];

const MISSION_ITEMS = [
  { key: "reveal",   icon: Sparkles },
  { key: "create",   icon: Rocket },
  { key: "develop",  icon: Award },
  { key: "support",  icon: TrendingUp },
  { key: "certify",  icon: ShieldCheck },
  { key: "employ",   icon: Users },
  { key: "innovate", icon: Lightbulb },
  { key: "build",    icon: Globe },
];

export default function About() {
  const { t } = useTranslation();

  return (
    <div className="afr-root" data-testid="afr-about-root">
      <SeoHead
        title="À propos"
        description="AFRICTAL, l'écosystème digital panafricain qui révèle et connecte les talents, entrepreneurs, entreprises et investisseurs africains autour d'une vision commune : construire une Afrique de production, de valeur et de fierté."
        path="/a-propos"
      />      <PublicNav />

      <div style={{ padding: "1.2rem 2rem 0" }}>
        <nav className="afr-breadcrumb" aria-label="breadcrumb" data-testid="about-breadcrumb">
          <Link to="/" data-testid="crumb-home">{t("common.breadcrumb_home")}</Link>
          <span className="afr-breadcrumb__sep">/</span>
          <span className="afr-breadcrumb__current">{t("about.breadcrumb")}</span>
        </nav>
      </div>

      {/* SECTION 1 — HERO */}
      <header className="afr-hero afr-hero--split" data-testid="afr-about-hero">
        <div className="afr-hero__grid">
          <div className="afr-hero__left">
            <div className="afr-hero__meta">
              <span className="afr-eyebrow">{t("about.hero.eyebrow")}</span>
            </div>
            <h1 className="afr-hero__title">
              {t("about.hero.title_1")} <em>{t("about.hero.title_2")}</em>
              <br />
              {t("about.hero.title_3")}
            </h1>
            <p className="afr-hero__lead">{t("about.hero.lead")}</p>

            <div className="afr-hero__cta-row" style={{ marginTop: "1.8rem" }}>
              <Link to="/login?mode=signup" className="afr-btn afr-btn--primary afr-btn--lg" data-testid="about-hero-cta-signup">
                <Rocket size={18} /> {t("about.hero.cta_join")}
              </Link>
              <a href="#pourquoi" className="afr-btn afr-btn--outline afr-btn--lg" data-testid="about-hero-cta-story">
                {t("about.hero.cta_story")}
              </a>
            </div>
          </div>

          <div className="afr-hero__right" aria-hidden="true">
            <div className="afr-hero-scene" data-testid="about-hero-scene" style={{ minHeight: 380 }}>
              <svg viewBox="0 0 480 380" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
                <defs>
                  <radialGradient id="aboutBg" cx="50%" cy="45%" r="60%">
                    <stop offset="0%" stopColor="rgba(0,150,64,0.10)" />
                    <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                  </radialGradient>
                </defs>
                <ellipse cx="240" cy="180" rx="200" ry="150" fill="url(#aboutBg)" />
                <path
                  d="M240 60 C 290 90, 320 140, 320 200 C 320 260, 280 300, 240 320 C 200 300, 160 260, 160 200 C 160 140, 190 90, 240 60 Z"
                  fill="rgba(0,150,64,0.06)"
                  stroke="rgba(0,150,64,0.4)"
                  strokeWidth="2"
                />
                {[
                  [200, 130], [270, 150], [180, 200], [290, 220], [220, 260], [255, 100],
                ].map(([cx, cy], i) => (
                  <circle key={i} cx={cx} cy={cy} r="5" fill="var(--afr-accent)" opacity="0.7" />
                ))}
              </svg>

              <div className="afr-hero-scene__personas">
                {[
                  { initials: "AB", labelKey: "student",      photo: "https://images.unsplash.com/photo-1508243529287-e21914733111?auto=format&fit=facearea&facepad=3&w=280&h=280&q=80", gradient: "linear-gradient(135deg, var(--mod-formation), var(--afr-accent))",       x: "12%", y: "12%" },
                  { initials: "KD", labelKey: "developer",    photo: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=facearea&facepad=3&w=280&h=280&q=80", gradient: "linear-gradient(135deg, var(--mod-freelance), var(--afr-accent))",      x: "60%", y: "10%" },
                  { initials: "MO", labelKey: "engineer",     photo: "https://images.unsplash.com/photo-1531384441138-2736e62e0919?auto=format&fit=facearea&facepad=3&w=280&h=280&q=80", gradient: "linear-gradient(135deg, var(--mod-talent), var(--afr-accent))",         x: "78%", y: "45%" },
                  { initials: "FN", labelKey: "entrepreneur", photo: "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?auto=format&fit=facearea&facepad=3&w=280&h=280&q=80", gradient: "linear-gradient(135deg, var(--mod-entrepreneur), var(--afr-accent))",   x: "18%", y: "58%" },
                  { initials: "SN", labelKey: "employer",     photo: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=facearea&facepad=3&w=280&h=280&q=80", gradient: "linear-gradient(135deg, var(--mod-emploi), var(--afr-accent))",         x: "62%", y: "62%" },
                  { initials: "TW", labelKey: "engineer",     photo: "https://images.unsplash.com/photo-1601288496920-b6154fe3626a?auto=format&fit=facearea&facepad=3&w=280&h=280&q=80", gradient: "linear-gradient(135deg, var(--mod-investissement), var(--afr-primary))", x: "40%", y: "80%" },
                ].map((p, idx) => (
                  <div key={idx} className="afr-hero-persona" style={{ left: p.x, top: p.y }} data-testid={`about-hero-persona-${idx}`}>
                    <div
                      className="afr-hero-persona__avatar afr-hero-persona__avatar--photo"
                      style={{ background: p.gradient, backgroundImage: `url(${p.photo}), ${p.gradient}`, backgroundSize: "cover", backgroundPosition: "center" }}
                    >
                      <span className="afr-hero-persona__initials" aria-hidden="true">{p.initials}</span>
                    </div>
                    <span className="afr-hero-persona__label">{t(`solutions.hero.personas.${p.labelKey}`)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* SECTION 2 — POURQUOI */}
      <section id="pourquoi" className="afr-section" data-testid="about-pourquoi">
        <h2 className="afr-section__title">{t("about.pourquoi_title")}</h2>
        <p className="afr-section__lead">{t("about.pourquoi_lead")}</p>

        <div className="afr-grid" style={{ gridTemplateColumns: "1fr 1.3fr", alignItems: "flex-start", gap: "3rem", marginTop: "1.5rem" }}>
          <div className="afr-card" style={{ padding: 0, overflow: "hidden", borderRadius: 20, minHeight: 520, background: "linear-gradient(180deg, color-mix(in oklab, var(--afr-primary) 8%, var(--afr-surface)), var(--afr-surface))" }} data-testid="about-pourquoi-illustration">
            <div style={{ padding: "1.6rem", position: "relative", height: "100%" }}>
              <span className="afr-eyebrow">{t("about.pourquoi.illustration_eyebrow")}</span>
              <h3 style={{ marginTop: "0.6rem", fontFamily: "'Outfit',sans-serif", fontSize: "1.4rem", lineHeight: 1.25 }}>
                {t("about.pourquoi.illustration_title_1")}
                <br />{t("about.pourquoi.illustration_title_2")}
              </h3>

              <div style={{ position: "relative", height: 360, marginTop: "1rem" }}>
                {[
                  { photo: "https://images.unsplash.com/photo-1508243529287-e21914733111?auto=format&fit=facearea&facepad=3&w=320&h=320&q=80", x: "8%",  y: "6%",  size: 96,  z: 3 },
                  { photo: "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?auto=format&fit=facearea&facepad=3&w=320&h=320&q=80", x: "55%", y: "0%",  size: 108, z: 4 },
                  { photo: "https://images.unsplash.com/photo-1531384441138-2736e62e0919?auto=format&fit=facearea&facepad=3&w=320&h=320&q=80", x: "0%",  y: "42%", size: 92,  z: 2 },
                  { photo: "https://images.unsplash.com/photo-1601288496920-b6154fe3626a?auto=format&fit=facearea&facepad=3&w=320&h=320&q=80", x: "55%", y: "45%", size: 100, z: 3 },
                  { photo: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=facearea&facepad=3&w=320&h=320&q=80", x: "28%", y: "60%", size: 104, z: 5 },
                ].map((it, i) => (
                  <div
                    key={i}
                    style={{
                      position: "absolute", left: it.x, top: it.y, width: it.size, height: it.size,
                      borderRadius: "50%",
                      backgroundImage: `url(${it.photo})`,
                      backgroundSize: "cover", backgroundPosition: "center",
                      boxShadow: "0 8px 22px rgba(15,42,66,0.18), 0 0 0 4px rgba(255,255,255,0.95)",
                      zIndex: it.z,
                      border: "2px solid rgba(255,255,255,0.9)",
                    }}
                  />
                ))}
              </div>

              <p className="afr-muted" style={{ fontSize: "0.85rem", position: "absolute", bottom: "1rem", left: "1.6rem", right: "1.6rem" }}>
                {t("about.pourquoi.illustration_caption")}
              </p>
            </div>
          </div>

          <div data-testid="about-pourquoi-story">
            <article className="afr-card" style={{ marginBottom: "1.4rem", borderLeft: "4px solid var(--mod-emploi)" }}>
              <div className="afr-card__badge" style={{ color: "var(--mod-emploi)" }}>{t("about.pourquoi.constat_badge")}</div>
              <p style={{ fontSize: "1.02rem", lineHeight: 1.7, marginTop: "0.6rem" }}>{t("about.pourquoi.constat_p1")}</p>
              <p style={{ fontSize: "1.02rem", lineHeight: 1.7, marginTop: "0.6rem" }}>{t("about.pourquoi.constat_p2")}</p>
              <p style={{ fontSize: "1.02rem", lineHeight: 1.7, marginTop: "0.6rem" }}>{t("about.pourquoi.constat_p3")}</p>
            </article>

            <article className="afr-card" style={{ marginBottom: "1.4rem", borderLeft: "4px solid var(--afr-primary)" }}>
              <div className="afr-card__badge" style={{ color: "var(--afr-primary)" }}>{t("about.pourquoi.vision_badge")}</div>
              <p style={{ fontSize: "1.02rem", lineHeight: 1.7, marginTop: "0.6rem" }}>{t("about.pourquoi.vision_p1")}</p>
              <p style={{ fontSize: "1.02rem", lineHeight: 1.7, marginTop: "0.6rem" }}>{t("about.pourquoi.vision_p2")}</p>
              <p style={{ fontSize: "1.02rem", lineHeight: 1.7, marginTop: "0.6rem" }}>{t("about.pourquoi.vision_p3")}</p>
            </article>

            <article className="afr-card" style={{ borderLeft: "4px solid var(--afr-accent)" }}>
              <div className="afr-card__badge" style={{ color: "var(--mod-entrepreneur)" }}>{t("about.pourquoi.ambition_badge")}</div>
              <p style={{ fontSize: "1.02rem", lineHeight: 1.7, marginTop: "0.6rem" }}>{t("about.pourquoi.ambition_p1")}</p>
              <p style={{ fontSize: "1.02rem", lineHeight: 1.7, marginTop: "0.6rem" }}>{t("about.pourquoi.ambition_p2")}</p>
            </article>
          </div>
        </div>
      </section>

      {/* SECTION 3 — MISSION */}
      <section id="mission" className="afr-section afr-section--dark" data-testid="about-mission" style={{
        background: "linear-gradient(135deg, var(--afr-secondary), color-mix(in oklab, var(--afr-secondary) 85%, var(--afr-primary)))",
        color: "#F7F5F0",
        borderRadius: 24,
        margin: "2rem",
        padding: "3.6rem 2.4rem",
      }}>
        <h2 className="afr-section__title" style={{ color: "#F7F5F0" }}>{t("about.mission_title")}</h2>
        <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "1.2rem", marginTop: "2rem" }} data-testid="about-mission-grid">
          {MISSION_ITEMS.map((m) => (
            <div key={m.key} className="afr-card" data-testid={`mission-item-${m.key}`}
                 style={{ background: "rgba(255,255,255,0.06)", borderColor: "rgba(255,255,255,0.14)", color: "#F7F5F0", padding: "1.2rem" }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: "linear-gradient(135deg, var(--afr-primary), var(--afr-accent))", display: "grid", placeItems: "center", color: "#fff" }}>
                <m.icon size={20} />
              </div>
              <div style={{ marginTop: "0.8rem", fontFamily: "'Outfit',sans-serif", fontSize: "1.05rem", fontWeight: 600 }}>{t(`about.mission.${m.key}`)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SECTION 4 — VISION */}
      <section id="vision" className="afr-section" data-testid="about-vision">
        <span className="afr-eyebrow">{t("about.vision_eyebrow")}</span>
        <h2 className="afr-section__title" style={{ marginTop: "0.6rem" }}>{t("about.vision_title")}</h2>
        <p className="afr-section__lead">{t("about.vision_lead")}</p>

        <ol className="afr-timeline" data-testid="about-vision-timeline" style={{ listStyle: "none", padding: 0, margin: "2rem 0 0", position: "relative" }}>
          {TIMELINE_YEARS.map((year) => (
            <li key={year} data-testid={`about-vision-step-${year}`} style={{
              display: "grid", gridTemplateColumns: "140px 1fr", gap: "1.4rem", alignItems: "flex-start",
              padding: "1.2rem 0", borderLeft: "2px solid var(--afr-primary)", paddingLeft: "1.4rem", marginLeft: "1rem",
              position: "relative",
            }}>
              <span aria-hidden="true" style={{
                position: "absolute", left: -10, top: "1.6rem", width: 18, height: 18, borderRadius: "50%",
                background: `linear-gradient(135deg, var(--afr-primary), var(--afr-accent))`, border: "3px solid #fff", boxShadow: "0 0 0 2px var(--afr-primary)",
              }} />
              <strong style={{ fontFamily: "'Outfit',sans-serif", fontSize: "1.8rem", color: "var(--afr-primary)" }}>{year}</strong>
              <div>
                <h3 style={{ fontFamily: "'Outfit',sans-serif", fontSize: "1.15rem", marginBottom: "0.3rem" }}>{t(`about.vision_timeline.${year}.title`)}</h3>
                <p className="afr-muted" style={{ fontSize: "0.94rem" }}>{t(`about.vision_timeline.${year}.desc`)}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* SECTION 5 — VALEURS */}
      <section id="valeurs" className="afr-section" data-testid="about-values">
        <h2 className="afr-section__title">{t("about.values_title")}</h2>
        <p className="afr-section__lead">{t("about.values_lead")}</p>
        <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "1.2rem", marginTop: "1.5rem" }}>
          {VALUE_KEYS.map((v) => (
            <article key={v.key} className="afr-persona" data-testid={`about-value-${v.key}`}>
              <div className="afr-persona__icon" style={{ background: `linear-gradient(135deg, ${v.color}, var(--afr-accent))` }}>
                <v.icon size={22} />
              </div>
              <h3>{t(`about.values.${v.key}.title`)}</h3>
              <p>{t(`about.values.${v.key}.desc`)}</p>
            </article>
          ))}
        </div>
      </section>

      {/* SECTION 6 — CTA */}
      <section className="afr-section" data-testid="about-cta">
        <div className="afr-cta-banner">
          <div className="afr-cta-banner__text">
            <h2>{t("about.cta_title")}</h2>
            <p>{t("about.cta_lead")}</p>
          </div>
          <div className="afr-cta-banner__actions">
            <Link
              to="/login?mode=signup"
              className="afr-btn afr-btn--primary afr-btn--lg"
              data-testid="about-cta-signup"
            >
              <Rocket size={16} /> {t("about.cta_signup")}
            </Link>
            <Link
              to="/login"
              className="afr-btn afr-btn--outline afr-btn--lg"
              style={{ color: "#F7F5F0", borderColor: "rgba(247,245,240,0.3)" }}
              data-testid="about-cta-login"
            >
              {t("about.cta_login")} <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
